import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import "../Assets/Css/Table.css";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Form } from "react-bootstrap";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosConfig } from "../axiosAPI";
import GlobalDataTable from "../components/GlobalDataTable";

function Driver() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [area, setArea] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverStatusStats, setDriverStatusStats] = useState({
    totalDrivers: 0,
    pendingDrivers: 0,
    approvedDrivers: 0,
    rejectedDrivers: 0,
  });
  const [statusStatsLoading, setStatusStatsLoading] = useState(true);
  const auth = useAuth();
  const driversTableRef = useRef(null);

  const defaultRowCount = 10;
  const rowCountOptions = [10, 20, 50, 100];
  const pageNoFromUrl = Math.max(1, parseInt(searchParams.get("pageNo") || "1", 10) || 1);
  const rowCountFromUrl = (() => {
    const n = parseInt(searchParams.get("rowCount") || String(defaultRowCount), 10) || defaultRowCount;
    return rowCountOptions.includes(n) ? n : defaultRowCount;
  })();
  const keywordFromUrl = searchParams.get("keyword") || "";
  const initialFirst = (pageNoFromUrl - 1) * rowCountFromUrl;

  const handleTableStateChange = useCallback(
    ({ pageNo, rows, keyword }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams();
          if (pageNo > 1) next.set("pageNo", String(pageNo));
          if (rows !== defaultRowCount) next.set("rowCount", String(rows));
          if (keyword) next.set("keyword", keyword);
          if (prev.toString() === next.toString()) return prev;
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const buildDriverListPath = useCallback(() => {
    const state = driversTableRef.current?.getState?.();
    const params = new URLSearchParams();
    const pageNo = state?.pageNo ?? pageNoFromUrl;
    const rows = state?.rows ?? rowCountFromUrl;
    const keyword = state?.keyword ?? keywordFromUrl;
    if (pageNo > 1) params.set("pageNo", String(pageNo));
    if (rows !== defaultRowCount) params.set("rowCount", String(rows));
    if (keyword) params.set("keyword", keyword);
    const qs = params.toString();
    return qs ? `/Driver?${qs}` : "/Driver";
  }, [pageNoFromUrl, rowCountFromUrl, keywordFromUrl]);

  const fetchDriversPage = useCallback(
    async ({ pageNo, rowCount, keyword }) => {
      try {
        const params = new URLSearchParams({
          pageNo: String(pageNo),
          rowCount: String(rowCount),
        });
        if (keyword) params.set("keyword", keyword);
        const response = await axiosConfig.get(
          `${process.env.REACT_APP_BASE_URL}/users/drivers?${params.toString()}`,
          {
            headers: {
              Authorization: "Bearer " + auth.token,
              "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
            },
          }
        );
        return {
          result: response.data.data.result,
          totalCount: response.data.data.totalCount,
        };
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
        return { result: [], totalCount: 0 };
      }
    },
    [auth.token]
  );

  const getArea = async () => {
    try {
      const response = await axiosConfig.get(`${process.env.REACT_APP_BASE_URL}/areas`, {
        headers: {
          Authorization: "Bearer " + auth.token,
          "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
        },
      });
      setArea(response.data.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  const fetchDriverStatusStats = useCallback(async () => {
    try {
      setStatusStatsLoading(true);
      const response = await axiosConfig.get(
        `${process.env.REACT_APP_BASE_URL}/dashboard/driver-application-status`,
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      setDriverStatusStats(response.data.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to load driver status statistics!",
      });
    } finally {
      setStatusStatsLoading(false);
    }
  }, [auth.token]);

  const pendingCount = drivers.filter(
    (driver) => driver.applicationStatusLocalized?.toLowerCase() === "pending"
  ).length;

  const driverToolbarExtras = (
    <>
      <div className="search-item">
        <label htmlFor="driverSelect" className="search-label">
          <i className="fa fa-user me-1"></i>
          Select Driver:
        </label>
        <Dropdown
          id="driverSelect"
          value={selectedDriver}
          onChange={(e) => {
            const v = e.value;
            setSelectedDriver(v);
            driversTableRef.current?.setSearch(v ? v.phoneNumber || v.fullName || "" : "");
          }}
          options={drivers}
          optionLabel="fullName"
          placeholder="Search and select a driver"
          filter
          filterPlaceholder="Search drivers..."
          showClear
          className="custom-dropdown"
          itemTemplate={(option) =>
            option ? (
              <div className="dropdown-item">
                <img
                  src={`http://dog.addictaco.com${option?.profilePicturePath}`}
                  alt={option.fullName || "Driver"}
                  className="dropdown-img"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="dropdown-content">
                  <div className="dropdown-name">{option.fullName || "Unknown Driver"}</div>
                  <div className="dropdown-details">{option.phoneNumber || "N/A"}</div>
                </div>
              </div>
            ) : (
              <span>No driver selected</span>
            )
          }
          valueTemplate={(option) =>
            option ? (
              <div className="selected-value">
                <img
                  src={`http://dog.addictaco.com${option?.profilePicturePath}`}
                  alt={option.fullName}
                  className="selected-img"
                />
                <span className="selected-text">{option.fullName}</span>
              </div>
            ) : (
              <span className="placeholder-text">Select a driver</span>
            )
          }
        />
      </div>
      <div className="search-item">
        <div className="pending-count-label">
          <i className="fa fa-clock me-1"></i>
          <span className="pending-text">Pending on page:</span>
          <span className="pending-count">{pendingCount}</span>
        </div>
      </div>
    </>
  );

  useEffect(() => {
    getArea();
    fetchDriverStatusStats();
  }, [auth.token, fetchDriverStatusStats]);

  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">Edit Driver</span>
    </div>
  );

  const footerContent = (
    <div className="modal-button">
      <Button label="Cancel" className="cancel" onClick={() => setVisible(false)} autoFocus />
      <Button
        label="Save"
        type="submit"
        onClick={() => {
          editUser();
          setVisible(false);
        }}
        className="save"
        autoFocus
      />
    </div>
  );

  const headerElement2 = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">Add Driver</span>
    </div>
  );
  
  const footerContent2 = (
    <div className="modal-button">
      <Button label="Cancel" className="cancel" onClick={() => setVisible2(false)} autoFocus />
      <Button
        label="Save"
        type="submit"
        onClick={() => {
          handleSubmit();
          setVisible2(false);
        }}
        className="save"
        autoFocus
      />
    </div>
  );

  const [formData, setFormData] = useState({
    FullName: "",
    PhoneNumber: "",
    Password: "",
    NationalID: "",
    CarModel: "",
    CarLicense: "",
    CarPlateNumber: "",
    CarColor: "",
    ProfilePictureFile: [],
    HaveADog: [],
    CoverageAreaUuid: [],
    FrontNationalIDFile: [],
    BackNationalIDFile: [],
    FrontDriverLicenseFile: [],
    BackDriverLicenseFile: [],
    FrontCarLicenseFile: [],
    BackCarLicenseFile: [],
    CriminalRecordFile: [],
    DrugTestFile: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    try {
      const response = await axiosConfig.post(`${process.env.REACT_APP_BASE_URL}/users/drivers/create`, formData, {
        headers: {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "multipart/form-data",
          "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
        },
      });
      driversTableRef.current?.reload();
      fetchDriverStatusStats();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver added successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
    setFormData({
      FullName: "",
      PhoneNumber: "",
      Password: "",
      NationalID: "",
      CarModel: "",
      CarLicense: "",
      CarPlateNumber: "",
      CarColor: "",
      ProfilePictureFile: [],
      HaveADog: [],
      CoverageAreaUuid: [],
      FrontNationalIDFile: [],
      BackNationalIDFile: [],
      FrontDriverLicenseFile: [],
      BackDriverLicenseFile: [],
      FrontCarLicenseFile: [],
      BackCarLicenseFile: [],
      CriminalRecordFile: [],
      DrugTestFile: [],
    });
  };

  const gotToNewPage = (rowData) => {
    return (
      <a
        className="edit"
        onClick={(e) => {
          const returnTo = buildDriverListPath();
          navigate(`/Details/${rowData.uuid}`, {
            state: { item: rowData, returnTo },
          });
        }}
      >
        details
      </a>
    );
  };

  const driverImg = (rowData) => {
    return (
      <img height="100px" src={`http://dog.addictaco.com${rowData?.profilePicturePath}`} alt="driver" className="driver-img" />
    );
  };

  const editUser = async () => {
    const formDataToSend = new FormData();

    formDataToSend.append("fullName", formData.fullName);
    formDataToSend.append("phoneNumber", formData.phoneNumber);
    formDataToSend.append("nationalID", formData.nationalID);
    formDataToSend.append("carModel", formData.carModel);
    formDataToSend.append("carLicense", formData.carLicense);
    formDataToSend.append("CarPlateNumber", formData.CarPlateNumber);
    formDataToSend.append("CarColor", formData.CarColor);
    formDataToSend.append("HaveADog", formData.haveADog || formData.HaveADog || false);
    formDataToSend.append("CoverageAreaUuid", formData.coverageAreaUuid || formData.CoverageAreaUuid || "");

    if (formData.ProfilePictureFile instanceof File) {
      formDataToSend.append("ProfilePictureFile", formData.ProfilePictureFile);
    }
    if (formData.FrontNationalIDFile instanceof File) {
      formDataToSend.append("FrontNationalIDFile", formData.FrontNationalIDFile);
    }
    if (formData.BackNationalIDFile instanceof File) {
      formDataToSend.append("BackNationalIDFile", formData.BackNationalIDFile);
    }
    if (formData.FrontDriverLicenseFile instanceof File) {
      formDataToSend.append("FrontDriverLicenseFile", formData.FrontDriverLicenseFile);
    }
    if (formData.BackDriverLicenseFile instanceof File) {
      formDataToSend.append("BackDriverLicenseFile", formData.BackDriverLicenseFile);
    }
    if (formData.FrontCarLicenseFile instanceof File) {
      formDataToSend.append("FrontCarLicenseFile", formData.FrontCarLicenseFile);
    }
    if (formData.BackCarLicenseFile instanceof File) {
      formDataToSend.append("BackCarLicenseFile", formData.BackCarLicenseFile);
    }
    if (formData.CriminalRecordFile instanceof File) {
      formDataToSend.append("CriminalRecordFile", formData.CriminalRecordFile);
    }
    if (formData.DrugTestFile instanceof File) {
      formDataToSend.append("DrugTestFile", formData.DrugTestFile);
    }

    try {
      const response = await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/drivers/${formData?.uuid}/update`,
        formDataToSend,
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "multipart/form-data",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      driversTableRef.current?.reload();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver updated successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  const blockUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/${uuid}/block`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      driversTableRef.current?.reload();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver blocked successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to block driver!",
      });
    }
  };

  const unBlockUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/${uuid}/unblock`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      driversTableRef.current?.reload();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver unblocked successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to unblock driver!",
      });
    }
  };

  const approveUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/drivers/${uuid}/approve`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      driversTableRef.current?.reload();
      fetchDriverStatusStats();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver approved successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to approve driver!",
      });
    }
  };

  const rejectUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/drivers/${uuid}/reject`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      driversTableRef.current?.reload();
      fetchDriverStatusStats();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver rejected successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to reject driver!",
      });
    }
  };

  const statusBodyTemplate = (rowData) => {
    return rowData.applicationStatus == "Pending" ? (
      <>
        <div className="action-table">
          <a
            className="approve"
            onClick={(e) => {
              approveUser(rowData.uuid);
            }}
          >
            <i className="fa fa-check"></i>
          </a>
          <a
            className="delete"
            onClick={(e) => {
              rejectUser(rowData.uuid);
            }}
          >
            <i className="fa fa-close"></i>
          </a>
        </div>
      </>
    ) : rowData.applicationStatus == "Approved" ? (
      <>
        <p>Approved</p>
      </>
    ) : (
      <>
        <p>Rejected</p>
      </>
    );
  };

  const statusAction = (rowData) => {
    return (
      <>
        <div className="action-table">
          <a
            className="edit"
            onClick={(e) => {
              setFormData({
                uuid: rowData.uuid,
                fullName: rowData.fullName,
                phoneNumber: rowData.phoneNumber,
                nationalID: rowData.nationalID,
                carModel: rowData.carModel,
                carLicense: rowData.carLicense,
                CarPlateNumber: rowData.carPlateNumber,
                CarColor: rowData.carColor || "#000000",
                haveADog: rowData.haveADog,
                coverageAreaUuid: rowData.coverageAreaUuid,
                ProfilePictureFile: [],
                FrontNationalIDFile: [],
                BackNationalIDFile: [],
                FrontDriverLicenseFile: [],
                BackDriverLicenseFile: [],
                FrontCarLicenseFile: [],
                BackCarLicenseFile: [],
                CriminalRecordFile: [],
                DrugTestFile: [],
                profilePicturePath: rowData.profilePicturePath,
                frontNationalIDPath: rowData.frontNationalIDPath,
                backNationalIDPath: rowData.backNationalIDPath,
                frontDriverLicensePath: rowData.frontDriverLicensePath,
                backDriverLicensePath: rowData.backDriverLicensePath,
                frontCarLicensePath: rowData.frontCarLicensePath,
                backCarLicensePath: rowData.backCarLicensePath,
                criminalRecordPath: rowData.criminalRecordPath,
                drugTestPath: rowData.drugTestPath
              });
              setVisible(true);
            }}
          >
            <i className="fa fa-pen"></i>
          </a>
          {rowData.stateLocalized == "Enabled" ? (
            <a
              className="delete"
              onClick={(e) => {
                blockUser(rowData.uuid);
              }}
            >
              <i className="fa fa-ban"></i>
            </a>
          ) : (
            <a
              className="delete"
              onClick={(e) => {
                unBlockUser(rowData.uuid);
              }}
            >
              <i className="fa fa-unlock"></i>
            </a>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="dev-table">
          <Navbar />
          <div className="driver-stats-cards">
            <div className="row">
              <div className="col-md-3 col-sm-6">
                <div className="card-details driver-stat-card driver-stat-total">
                  <h2>
                    Total Drivers
                    <span>{statusStatsLoading ? "—" : driverStatusStats.totalDrivers}</span>
                  </h2>
                </div>
              </div>
              <div className="col-md-3 col-sm-6">
                <div className="card-details driver-stat-card driver-stat-pending">
                  <h2>
                    Pending
                    <span>{statusStatsLoading ? "—" : driverStatusStats.pendingDrivers}</span>
                  </h2>
                </div>
              </div>
              <div className="col-md-3 col-sm-6">
                <div className="card-details driver-stat-card driver-stat-approved">
                  <h2>
                    Approved
                    <span>{statusStatsLoading ? "—" : driverStatusStats.approvedDrivers}</span>
                  </h2>
                </div>
              </div>
              <div className="col-md-3 col-sm-6">
                <div className="card-details driver-stat-card driver-stat-rejected">
                  <h2>
                    Rejected
                    <span>{statusStatsLoading ? "—" : driverStatusStats.rejectedDrivers}</span>
                  </h2>
                </div>
              </div>
            </div>
          </div>
          <div className="table-card">
            <div className="table">
              <a className="add">
                <i
                  className="fa fa-plus"
                  onClick={(e) => {
                    setVisible2(true);
                  }}
                ></i>
              </a>
              <GlobalDataTable
                ref={driversTableRef}
                fetchPage={fetchDriversPage}
                defaultRowCount={defaultRowCount}
                rowCountOptions={rowCountOptions}
                initialFirst={initialFirst}
                initialRows={rowCountFromUrl}
                initialSearch={keywordFromUrl}
                onTableStateChange={handleTableStateChange}
                searchPlaceholder="Search drivers by name, phone, car, status…"
                emptyMessage="No drivers found."
                headerTitle={
                  <span>
                    <i className="fa fa-users me-2"></i>
                    Driver Management
                  </span>
                }
                toolbarExtras={driverToolbarExtras}
                onRowsChange={setDrivers}
                dataKey="uuid"
                showGridlines
                tableStyle={{ minWidth: "700px" }}
              >
                <Column field="profilePicturePath" header="Driver Picture" body={driverImg}></Column>
                <Column field="fullName" header="Driver Name" style={{ width: "14.28%" }}></Column>
                <Column field="carModel" header="Car model" style={{ width: "14.28%" }}></Column>
                <Column field="carLicense" header="Car License " style={{ width: "14.28%" }}></Column>
                <Column field="dateCreated" header="Created At" style={{ width: "14.28%" }}></Column>
                <Column field="nationalID" header="national ID" style={{ width: "14.28%" }}></Column>
                <Column
                  field="applicationStatusLocalized"
                  header="Application Status"
                  style={{ width: "14.28%" }}
                  body={statusBodyTemplate}
                ></Column>
                <Column field="uuid" body={gotToNewPage} header="Details" style={{ width: "14.28%" }}></Column>
                <Column field="uuid" body={statusAction} header="" style={{ width: "14.28%" }}></Column>
              </GlobalDataTable>
              
              {/* Add Driver Dialog */}
              <Dialog
                visible={visible2}
                modal
                header={headerElement2}
                footer={footerContent2}
                style={{ width: "50rem" }}
                onHide={() => {
                  if (!visible2) return;
                  setVisible2(false);
                }}
              >
                <Form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Profile Picture</Form.Label>
                        <Form.Control
                          id="ProfilePictureFile"
                          className="file"
                          type="file"
                          name="ProfilePictureFile"
                          placeholder="Upload profile picture"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="FullName"
                          placeholder="Saqer"
                          value={formData.FullName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="PhoneNumber"
                          placeholder="01143987449"
                          value={formData.PhoneNumber}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="Password"
                          placeholder="******* **"
                          value={formData.Password}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>National ID</Form.Label>
                        <Form.Control
                          type="number"
                          name="NationalID"
                          placeholder="**** **** *888"
                          value={formData.NationalID}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car model</Form.Label>
                        <Form.Control
                          type="text"
                          name="CarModel"
                          placeholder="2014"
                          value={formData.CarModel}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car License</Form.Label>
                        <Form.Control
                          type="text"
                          name="CarLicense"
                          placeholder="car license"
                          value={formData.CarLicense}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car Plate Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="CarPlateNumber"
                          placeholder="Enter car plate number"
                          value={formData.CarPlateNumber}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car Color</Form.Label>
                        <Form.Control
                          type="color"
                          name="CarColor"
                          placeholder="Select car color"
                          value={formData.CarColor || "#000000"}
                          onChange={handleChange}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Coverage Area</Form.Label>
                        <select 
                          onChange={handleChange} 
                          name="CoverageAreaUuid" 
                          className="form-select"
                          value={formData.CoverageAreaUuid}
                        >
                          <option value="">Select Coverage Area</option>
                          {area.map((x, index) => (
                            <option value={x.uuid} key={index}>
                              {x.displayName}
                            </option>
                          ))}
                        </select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Have A Dog</Form.Label>
                        <select 
                          onChange={handleChange} 
                          name="HaveADog" 
                          className="form-select"
                          value={formData.HaveADog}
                        >
                          <option value={true}>Yes</option>
                          <option value={false}>No</option>
                        </select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front National ID File</Form.Label>
                        <Form.Control
                          id="FrontNationalIDFile"
                          className="file"
                          type="file"
                          name="FrontNationalIDFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back National ID File</Form.Label>
                        <Form.Control
                          id="BackNationalIDFile"
                          className="file"
                          type="file"
                          name="BackNationalIDFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front Driver License File</Form.Label>
                        <Form.Control
                          id="FrontDriverLicenseFile"
                          className="file"
                          type="file"
                          name="FrontDriverLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back Driver License File</Form.Label>
                        <Form.Control
                          id="BackDriverLicenseFile"
                          className="file"
                          type="file"
                          name="BackDriverLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front Car License File</Form.Label>
                        <Form.Control
                          id="FrontCarLicenseFile"
                          className="file"
                          type="file"
                          name="FrontCarLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back Car License File</Form.Label>
                        <Form.Control
                          id="BackCarLicenseFile"
                          className="file"
                          type="file"
                          name="BackCarLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Criminal Record File</Form.Label>
                        <Form.Control
                          id="CriminalRecordFile"
                          className="file"
                          type="file"
                          name="CriminalRecordFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Drug Test File</Form.Label>
                        <Form.Control
                          id="DrugTestFile"
                          className="file"
                          type="file"
                          name="DrugTestFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </Form>
              </Dialog>

              {/* Edit Driver Dialog */}
              <Dialog
                visible={visible}
                modal
                header={headerElement}
                footer={footerContent}
                style={{ width: "50rem" }}
                onHide={() => {
                  if (!visible) return;
                  setVisible(false);
                }}
              >
                <Form>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Profile Picture</Form.Label>
                        <Form.Control
                          id="ProfilePictureFile"
                          className="file"
                          type="file"
                          name="ProfilePictureFile"
                          placeholder="Upload profile picture"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="fullName"
                          placeholder="Saqer"
                          value={formData.fullName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phoneNumber"
                          placeholder="01143987449"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>National ID</Form.Label>
                        <Form.Control
                          type="number"
                          name="nationalID"
                          placeholder="**** **** *888"
                          value={formData.nationalID}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car model</Form.Label>
                        <Form.Control
                          type="text"
                          name="carModel"
                          placeholder="2014"
                          value={formData.carModel}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car License</Form.Label>
                        <Form.Control
                          type="text"
                          name="carLicense"
                          placeholder="car license"
                          value={formData.carLicense}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car Plate Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="CarPlateNumber"
                          placeholder="Enter car plate number"
                          value={formData.CarPlateNumber}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Car Color</Form.Label>
                        <Form.Control
                          type="color"
                          name="CarColor"
                          placeholder="Select car color"
                          value={formData.CarColor || "#000000"}
                          onChange={handleChange}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Coverage Area</Form.Label>
                        <select 
                          onChange={handleChange} 
                          name="CoverageAreaUuid" 
                          className="form-select"
                          value={formData.coverageAreaUuid}
                        >
                          <option value="">Select Coverage Area</option>
                          {area.map((x, index) => (
                            <option value={x.uuid} key={index}>
                              {x.displayName}
                            </option>
                          ))}
                        </select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Have A Dog</Form.Label>
                        <select 
                          onChange={handleChange} 
                          name="HaveADog" 
                          className="form-select"
                          value={formData.haveADog}
                        >
                          <option value={true}>Yes</option>
                          <option value={false}>No</option>
                        </select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front National ID File</Form.Label>
                        <Form.Control
                          id="FrontNationalIDFile"
                          className="file"
                          type="file"
                          name="FrontNationalIDFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back National ID File</Form.Label>
                        <Form.Control
                          id="BackNationalIDFile"
                          className="file"
                          type="file"
                          name="BackNationalIDFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front Driver License File</Form.Label>
                        <Form.Control
                          id="FrontDriverLicenseFile"
                          className="file"
                          type="file"
                          name="FrontDriverLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back Driver License File</Form.Label>
                        <Form.Control
                          id="BackDriverLicenseFile"
                          className="file"
                          type="file"
                          name="BackDriverLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Front Car License File</Form.Label>
                        <Form.Control
                          id="FrontCarLicenseFile"
                          className="file"
                          type="file"
                          name="FrontCarLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Back Car License File</Form.Label>
                        <Form.Control
                          id="BackCarLicenseFile"
                          className="file"
                          type="file"
                          name="BackCarLicenseFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Criminal Record File</Form.Label>
                        <Form.Control
                          id="CriminalRecordFile"
                          className="file"
                          type="file"
                          name="CriminalRecordFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Drug Test File</Form.Label>
                        <Form.Control
                          id="DrugTestFile"
                          className="file"
                          type="file"
                          name="DrugTestFile"
                          placeholder="text"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </Form>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Driver;
